import { useAuth } from './useAuth';
import { useBannerDismissalsStore } from '@/stores';
import type { BannerType, DismissMode } from '@/config/banners';

export interface DismissArgs {
  type: BannerType;
  dismiss: DismissMode;
  defaultSnoozeDays?: number;
}

export interface ResetArgs {
  type: BannerType;
}

export interface UseDismissBannerResult {
  dismiss: (args: DismissArgs) => Promise<void>;
  reset: (args: ResetArgs) => Promise<void>;
  isPending: boolean;
}

export function useDismissBanner(): UseDismissBannerResult {
  const { user } = useAuth();
  const upsert = useBannerDismissalsStore((s) => s.upsert);
  const remove = useBannerDismissalsStore((s) => s.remove);
  const isPending = useBannerDismissalsStore((s) => s.mutating);

  const dismiss = async (args: DismissArgs): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    if (args.dismiss === 'none') {
      throw new Error(`Banner ${args.type} cannot be dismissed`);
    }
    await upsert({
      bannerType: args.type,
      userId: user.id,
      snoozeDays:
        args.dismiss === 'snooze' ? args.defaultSnoozeDays : undefined,
    });
  };

  const reset = async (args: ResetArgs): Promise<void> => {
    if (!user) throw new Error('Not authenticated');
    await remove({ bannerType: args.type, userId: user.id });
  };

  return { dismiss, reset, isPending };
}
